import {ViewOptions} from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';

import {MapType} from 'constants/MapsConstants';
import {CircularMapState} from 'molbio/maps/MapStates';
import CircularOptions from 'molbio/maps/options/CircularOptions';
import {MapSelectorProps} from 'molbio/maps/options/MapSelector';
import SequenceOptionsView, {SequenceOptionsBaseProps} from 'molbio/maps/options/SequenceOptionsView';
import SimplifiedCircularOptions from 'molbio/maps/options/SimplifiedCircularOptions';
import {PanSliderProps, ZoomSliderProps} from 'molbio/maps/options/Sliders';
import {circularPaginate} from 'molbio/pdf/Paginate';
import CircularMapTabState from 'molbio/tabstates/CircularMapTabState';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {FeatureCollectionName} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

export interface CircularSequenceOptionsProps extends SequenceOptionsBaseProps {
  zoomSliderProps: ZoomSliderProps;
  panSliderProps: PanSliderProps;
  shownAnnotationsCount?: number;
  visibleAnnotationsCount?: number;
  // TODO(BNCH-52942): Warn when translations are automatically hidden, like we do for annotations.
  shownTranslationsCount?: number;
  visibleTranslationsCount?: number;
}

export class CircularMapTabOptionsView extends SequenceOptionsView<CircularMapState, CircularMapTabState> {
  previewSourceName = 'plasmid';
  previewPaginate = circularPaginate;

  trackedFeatureCollections(): Array<FeatureCollectionName> {
    return ['annotations', 'translations', 'parts', 'sequencePrimers'];
  }

  getProps(): CircularSequenceOptionsProps {
    const sequence = this.mapstate.sequence();
    const length = sequence.length();
    return {
      ...super.getProps(),
      sequenceId: sequence.id,
      zoomSliderProps: {
        min: this.mapstate.get('minZoom'),
        max: this.mapstate.get('maxZoom'),
        value: this.mapstate.get('zoom'),
        step: this.mapstate.get('stepZoom'),
        disabled: false,
        onChange: (zoom: number) => this.mapstate.setZoom(zoom),
        zoomBigger: () => this.mapstate.smoothZoomByStep(5),
        zoomSmaller: () => this.mapstate.smoothZoomByStep(-5),
      },
      panSliderProps: {
        min: -Math.floor(length / 2),
        max: Math.floor(length / 2),
        value: this.calculateOffset(),
        step: 1,
        disabled: false,
        onChange: (offset: number) => this.mapstate.setOffset(offset),
        panLeft: () => this.mapstate.smoothChangePanByStep(-this.calculatePanJump()),
        panRight: () => this.mapstate.smoothChangePanByStep(this.calculatePanJump()),
      },
      shownAnnotationsCount: this.mapstate.get('shownA'),
      visibleAnnotationsCount: this.mapstate.get('visibleA'),
      shownTranslationsCount: this.mapstate.get('shownTranslations'),
      visibleTranslationsCount: this.mapstate.get('visibleTranslations'),
    };
  }

  calculatePanJump(): number {
    const left = this.mapstate.get('leftBP');
    const right = this.mapstate.get('rightBP');
    const len = this.mapstate.get('sequence').length();
    let range = (right - left + len) % len;
    if (range === 0) {
      range = len;
    }
    const maxDegree = 20;
    const maxChange = (maxDegree / 360) * len;
    const change = Math.min(Math.max(1, range / 4), maxChange);
    return change;
  }

  calculateOffset(): number {
    const length = this.mapstate.sequence().length();
    let offset = this.mapstate.get('DNAOffset') % length;
    if (offset < -length / 2) {
      offset += length;
    }
    if (offset > length / 2) {
      offset -= length;
    }
    return offset;
  }

  handleBigSequence(): void {
    const isReallyBig = this.mapstate.sequence().isReallyBig();
    const cutsitesVisibilityOption = this.model.get('visibilityOptions').getOption(vo.CUTSITES.name)!;
    const orfsVisibilityOption = this.model.get('visibilityOptions').getOption(vo.ORFS.name)!;

    cutsitesVisibilityOption.set('disabled', isReallyBig, {noSave: true});
    orfsVisibilityOption.set('disabled', isReallyBig, {noSave: true});
    if (isReallyBig) {
      cutsitesVisibilityOption.set('visible', false, {noSave: true});
      orfsVisibilityOption.set('visible', false, {noSave: true});
    }
  }

  initialize(options?: ViewOptions<CircularMapTabState>): void {
    super.initialize(options);

    this.listenTo(
      this.mapstate,
      'change:zoom change:maxZoom change:minZoom change:stepZoom change:DNAOffset ' +
        'change:shownA change:visibleA change:totalA ' +
        'change:shownTranslations change:visibleTranslations change:totalTranslations',
      () => this.render()
    );
    this.listenTo(this.mapstate.sequence().get('dna'), 'change:forward', () => this.render());
  }

  remove(): this {
    ReactDOM.unmountComponentAtNode(this.el);
    super.remove();
    return this;
  }

  render(): this {
    renderToDOM(<CircularOptions {...this.getProps()} />, this.el);
    return this;
  }
}

export type SimplifiedCircularSequenceOptionsProps = CircularSequenceOptionsProps & MapSelectorProps;

export class SimplifiedCircularMapTabOptionsView extends CircularMapTabOptionsView {
  getProps(): SimplifiedCircularSequenceOptionsProps {
    return {
      ...super.getProps(),
      mapType: MapType.PLASMID,
      onSelectMapType: (mapType: MapType) => {
        // Emit special event that we can listen to from React.
        this.mapstate.sequence().trigger('changeMapType', mapType);
      },
      isCircular: this.mapstate.sequence().circular(),
    };
  }

  render(): this {
    renderToDOM(<SimplifiedCircularOptions {...this.getProps()} />, this.el);
    return this;
  }
}
